import type { IconAttributeValue, IconNode } from '@source-components-react/types';

/**
 * The placeholder a freshly dropped icon shows before the author picks one:
 * Lucide's `shapes`, inlined as a literal.
 *
 * It is a literal rather than a registry lookup on purpose. Nothing on the
 * render path may import `lucide-static` — the default library is registered
 * eagerly by every consumer, so its bundle reaches every customer page.
 * `__tests__/default-attribute-values.test.ts` checks this literal still
 * matches the registry, so a drift shows up as a test failure rather than a
 * placeholder that quietly stops matching the real icon.
 */
export const PLACEHOLDER_ICON_NAME = 'shapes';

export const PLACEHOLDER_ICON_NODE: IconNode = [
  [
    'path',
    { d: 'M8.3 10a.7.7 0 0 1-.626-1.079L11.4 3a.7.7 0 0 1 1.198-.043L16.3 8.9a.7.7 0 0 1-.572 1.1Z' },
  ],
  ['rect', { x: '3', y: '14', width: '7', height: '7', rx: '1' }],
  ['circle', { cx: '17.5', cy: '17.5', r: '3.5' }],
];

export const PLACEHOLDER_ICON: IconAttributeValue = {
  name: PLACEHOLDER_ICON_NAME,
  iconNode: PLACEHOLDER_ICON_NODE,
};

const isIconNode = (value: unknown): value is IconNode =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    (entry) =>
      Array.isArray(entry) &&
      entry.length >= 2 &&
      typeof entry[0] === 'string' &&
      entry[1] != null &&
      typeof entry[1] === 'object' &&
      !Array.isArray(entry[1])
  );

/**
 * Read geometry out of a persisted icon attribute.
 *
 * The composer writes `{ name, iconNode }` when the author picks an icon, so
 * every surface renders from the value alone and never needs the registry.
 * Anything else — a bare name string from a hand-edited config, a half-written
 * value — yields null and renders nothing, which is the specified behaviour
 * for an unresolvable icon on a published page.
 */
export const resolveIconAttributeValue = (value: unknown): IconNode | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const { iconNode } = value as { iconNode?: unknown };
  return isIconNode(iconNode) ? iconNode : null;
};
